import { createFileRoute, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, XCircle } from "lucide-react";
import type { RouteAudit, SeoAuditResult } from "@/lib/seoAudit";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/seo-audit")({
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  head: () => ({
    meta: [
      { title: "Internal SEO Audit | Ajility" },
      { name: "description", content: "Internal tool: sitemap, robots, canonical and schema checks." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SeoAuditPage,
});

function StatusPill({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
        ok ? "bg-emerald-500/10 text-emerald-700" : "bg-destructive/10 text-destructive"
      }`}
    >
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
      {label}
    </span>
  );
}

function RouteRow({ route }: { route: RouteAudit }) {
  const [open, setOpen] = useState(false);
  const clean = route.issues.length === 0;

  return (
    <div className="border-b border-border/60 last:border-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left hover:bg-muted/40"
        aria-expanded={open}
      >
        <span className="flex min-w-0 items-center gap-3">
          {clean ? (
            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
          ) : (
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
          )}
          <span className="truncate font-mono text-sm">{route.path}</span>
        </span>
        <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
          <span>{route.status ?? "—"}</span>
          <span>{route.schemas.length} schema</span>
          <span className={clean ? "text-emerald-600" : "text-amber-600"}>
            {clean ? "OK" : `${route.issues.length} issue${route.issues.length > 1 ? "s" : ""}`}
          </span>
        </span>
      </button>

      {open && (
        <div className="space-y-3 bg-muted/30 px-4 pb-4 pt-1 text-sm">
          <dl className="grid gap-2 sm:grid-cols-2">
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Title ({route.titleLength})</dt>
              <dd className="break-words">{route.title ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                Description ({route.descriptionLength})
              </dt>
              <dd className="break-words">{route.description ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Canonical</dt>
              <dd className="break-all font-mono text-xs">{route.canonical ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">og:image</dt>
              <dd className="break-all font-mono text-xs">{route.ogImage ?? "—"}</dd>
            </div>
          </dl>

          <div className="flex flex-wrap gap-2">
            <StatusPill ok={route.inSitemap} label={route.inSitemap ? "In sitemap" : "Not in sitemap"} />
            <StatusPill ok={route.robotsAllowed} label={route.robotsAllowed ? "Crawlable" : "Blocked"} />
            <StatusPill ok={route.canonicalOk} label={route.canonicalOk ? "Canonical self" : "Canonical mismatch"} />
            <StatusPill ok={route.indexable} label={route.indexable ? "Indexable" : "noindex"} />
          </div>

          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Schema.org nodes</p>
            {route.schemas.length === 0 ? (
              <p className="text-muted-foreground">None found</p>
            ) : (
              <ul className="mt-1 flex flex-wrap gap-2">
                {route.schemas.map((s, i) => (
                  <li
                    key={`${s.type}-${i}`}
                    className={`rounded-md px-2 py-1 text-xs ${
                      s.missing.length
                        ? "bg-destructive/10 text-destructive"
                        : "bg-emerald-500/10 text-emerald-700"
                    }`}
                  >
                    {s.type}
                    {s.missing.length ? ` — missing ${s.missing.join(", ")}` : ""}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {route.issues.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Issues</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-destructive">
                {route.issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SeoAuditPage() {
  const [data, setData] = useState<SeoAuditResult | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setIsPending(true);
    setError(null);
    try {
      const { runSeoAudit } = await import("@/lib/seoAudit");
      setData(await runSeoAudit(window.location.origin));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Audit failed");
    } finally {
      setIsPending(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-16">
      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Internal tool</p>
        <h1 className="mt-2 text-3xl font-bold">SEO audit</h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Crawls every route on this deployment and checks sitemap coverage, robots.txt access,
          canonical tags and schema.org validity. This page is noindex and not linked publicly.
        </p>
        <Button onClick={run} disabled={isPending} className="mt-5">
          {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          {isPending ? "Auditing…" : data ? "Re-run audit" : "Run audit"}
        </Button>
      </header>

      {error && (
        <p role="alert" className="rounded-md bg-destructive/10 p-4 text-destructive">
          {error}
        </p>
      )}

      {data && (
        <div className="space-y-8">
          <section className="grid gap-4 sm:grid-cols-4">
            {[
              { label: "Routes checked", value: data.summary.total },
              { label: "Clean", value: data.summary.passing },
              { label: "With issues", value: data.summary.withIssues },
              { label: "Total issues", value: data.summary.totalIssues },
            ].map((stat) => (
              <div key={stat.label} className="rounded-lg border border-border bg-card p-4">
                <p className="text-2xl font-bold">{stat.value}</p>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </section>

          <section className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-lg font-semibold">robots.txt</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              <StatusPill ok={data.robots.ok} label={`HTTP ${data.robots.status ?? "—"}`} />
              <StatusPill
                ok={data.robots.hasSitemapDirective}
                label={data.robots.hasSitemapDirective ? "Sitemap directive" : "No sitemap directive"}
              />
              <StatusPill ok={!data.robots.blocksEverything} label={data.robots.blocksEverything ? "Blocks all crawlers" : "Crawlers allowed"} />
            </div>
            <pre className="mt-3 overflow-x-auto rounded-md bg-muted p-3 text-xs">{data.robots.body || "—"}</pre>
          </section>

          <section className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-lg font-semibold">sitemap.xml</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              <StatusPill ok={data.sitemap.ok} label={`HTTP ${data.sitemap.status ?? "—"}`} />
              <StatusPill ok={data.sitemap.urlCount > 0} label={`${data.sitemap.urlCount} URLs`} />
              <StatusPill ok={data.sitemap.duplicates.length === 0} label={`${data.sitemap.duplicates.length} duplicates`} />
              <StatusPill ok={data.sitemap.missingRoutes.length === 0} label={`${data.sitemap.missingRoutes.length} routes missing`} />
              <StatusPill ok={data.sitemap.orphanEntries.length === 0} label={`${data.sitemap.orphanEntries.length} dead entries`} />
            </div>
            {data.sitemap.missingRoutes.length > 0 && (
              <p className="mt-3 text-sm">
                <span className="font-medium">Routes not in sitemap:</span>{" "}
                <span className="font-mono text-xs">{data.sitemap.missingRoutes.join(", ")}</span>
              </p>
            )}
            {data.sitemap.orphanEntries.length > 0 && (
              <p className="mt-2 text-sm">
                <span className="font-medium">Sitemap entries that do not return 200:</span>{" "}
                <span className="font-mono text-xs">{data.sitemap.orphanEntries.join(", ")}</span>
              </p>
            )}
          </section>

          <section className="overflow-hidden rounded-lg border border-border bg-card">
            <h2 className="border-b border-border px-4 py-3 text-lg font-semibold">
              Per-route checks
            </h2>
            {data.routes.map((route) => (
              <RouteRow key={route.path} route={route} />
            ))}
          </section>

          <p className="text-xs text-muted-foreground">
            Audited {data.baseUrl} at {new Date(data.generatedAt).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
}
